import { useEffect, useRef, useState } from 'react';
import { ACCEPT_ATTRIBUTE, kilobytes, toDataUrl } from './image.ts';
import { ImageProblem, prepareImage, type PreparedImage } from './prepareImage.ts';

interface ImagePickerProps {
  /** Unique on the page: the console and the platform can both show a picker. */
  readonly id: string;
  readonly value: PreparedImage | null;
  readonly onChange: (image: PreparedImage | null) => void;
  readonly disabled: boolean;
  /** Told while an image is being prepared, so the form can hold its submit. */
  readonly onBusy?: (busy: boolean) => void;
}

/**
 * One optional screenshot. The preview is of the prepared image, not the file
 * as chosen, so what the person sees is what support will see.
 */
export const ImagePicker = ({ id, value, onChange, disabled, onBusy }: ImagePickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preparing, setPreparing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    // The form cleared the image after sending; the input must forget the file
    // too, or choosing the same screenshot again fires no change event.
    if (value === null && inputRef.current) inputRef.current.value = '';
  }, [value]);

  const choose = async (file: File | null) => {
    setProblem(null);
    if (file === null) return;
    setPreparing(true);
    onBusy?.(true);
    try {
      onChange(await prepareImage(file));
    } catch (cause) {
      onChange(null);
      setProblem(cause instanceof ImageProblem ? cause.message : 'That image could not be prepared.');
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setPreparing(false);
      onBusy?.(false);
    }
  };

  const hintId = `${id}-help`;
  const problemId = `${id}-problem`;

  return (
    <div className="field sp-image">
      <label htmlFor={id}>Screenshot (optional)</label>
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        disabled={disabled || preparing}
        aria-describedby={problem === null ? hintId : `${hintId} ${problemId}`}
        aria-invalid={problem === null ? undefined : true}
        onChange={(event) => void choose(event.target.files?.[0] ?? null)}
      />
      <span className="hint" id={hintId}>
        One PNG, JPEG or WebP image. It is resized and re-encoded in your browser before it is sent,
        which also removes location and camera details stored in the file.
      </span>
      {preparing && (
        <p className="status-inline" role="status">
          Preparing the image…
        </p>
      )}
      {problem !== null && (
        <p className="sp-field-error" id={problemId} role="alert">
          {problem}
        </p>
      )}
      {value !== null && (
        <div className="sp-preview">
          <img src={toDataUrl(value.attachment)} alt="Preview of the screenshot that will be attached" />
          <span className="note num">{kilobytes(value.blob.size)}</span>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              inputRef.current?.focus();
            }}
          >
            Remove screenshot
          </button>
        </div>
      )}
    </div>
  );
};
